import { Chip, Input, Modal, Spinner } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiFetch } from '@/lib/api/client';
import type { ListResponse } from '@/lib/api/resource';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { hasContent, valueFor } from '@/lib/forms/translatable';
import type { DocumentRow } from '@/routes/documents/types';

/**
 * The document library, as a dialog — docs/05-ADMIN-DASHBOARD.md §4.2's "document
 * link (from library)".
 *
 * Search and pick, and nothing else: unlike the media dialog there is no upload
 * here, because §5.4's upload is a multi-file flow with a metadata step and
 * squeezing it into a link button would be offering a worse version of a screen
 * that already exists.
 *
 * What the row shows is what an editor needs to tell two similarly-named
 * regulations apart: the title, the year, the file type and the size. The id is
 * absent on purpose — an editor should never need to know one, which is the whole
 * reason this dialog exists.
 */
export function DocumentLibraryDialog({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (document: DocumentRow) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const [search, setSearch] = useState('');

  const documents = useQuery({
    queryKey: ['admin', 'documents', 'picker', search],
    queryFn: () =>
      apiFetch<ListResponse<DocumentRow>>('/admin/documents', {
        query: { search: search.trim() === '' ? undefined : search.trim(), per_page: 25 },
      }),
  });

  const rows = documents.data?.data ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title={t('editor.richText.documentPick')}
      closeLabel={t('common.close')}
      className="sm:w-[min(94vw,720px)]"
    >
      <div className="space-y-4">
        <Input
          label={t('editor.richText.documentSearch')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {documents.isPending ? (
          <p className="flex items-center gap-2 text-body-sm text-secondary">
            <Spinner size="sm" label={t('common.loading')} />
            {t('common.loading')}
          </p>
        ) : null}

        {!documents.isPending && rows.length === 0 ? (
          <p className="text-body-sm text-muted">{t('editor.richText.documentEmpty')}</p>
        ) : null}

        <ul className="max-h-80 divide-y divide-subtle overflow-auto rounded-card border border-subtle">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="hover:bg-surface-subtle flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                onClick={() => onPick(row)}
              >
                <span className="min-w-0">
                  <span className="block truncate text-body-sm font-semibold text-primary">
                    {/*
                      Georgian when there is no title in the editor's locale.
                      `valueFor` answers a missing translation with `''`, so an
                      editor working in English would otherwise be picking from a
                      list of blank rows — 1,714 imported documents have no
                      English title, which is most of the library.
                    */}
                    {valueFor(row.title, hasContent(row.title, locale) ? locale : 'ka')}
                  </span>
                  <span className="block truncate text-caption text-muted">{row.file_name}</span>
                </span>

                <span className="flex shrink-0 items-center gap-2">
                  {row.year === null ? null : (
                    <span className="text-caption text-secondary">{row.year}</span>
                  )}
                  <Chip tone="slate">{row.file_type.toUpperCase()}</Chip>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
